/**
 * Tagesdetail der Preisautomatik im Monats-Editor: jedes Feld des bisherigen DP-Tagesdetails
 * (aktuell, Ziel, Rückmeldung mit „Bestätigt am“, Zuständigkeit mit „wieder automatisch“,
 * Technische Details) – dieselbe Darstellung wie im Preisautomatik-Kalender, nicht nachgebaut.
 */
import { DynamicPricingDayDetailsBody } from '@/features/dynamic-pricing/calendar/DynamicPricingDayDetailsBody';
import { useDynamicPricingCalendarDetail } from '@/features/dynamic-pricing/calendar/useDynamicPricingCalendarDetail';
import { useDynamicPricingOwnershipRelease } from '@/features/dynamic-pricing/calendar/useDynamicPricingOwnershipRelease';
import {
  problemText,
  useApiProblem,
  type DynamicPricingCalendarApartment,
  type DynamicPricingCalendarDayDetailResponse,
} from '@/shared/api';
import { useAutomationLayer } from './AutomationLayerContext';

interface Props {
  propertyGroupId: string;
  propertyId: string;
  date: string;
  /** „Wieder automatisch steuern“ prüft `AvailabilityDistribution.Edit`, nicht die Rolle. */
  canEdit: boolean;
}

export function MonthDayDetail({ propertyGroupId, propertyId, date, canEdit }: Props) {
  const layer = useAutomationLayer();
  const detail = useDynamicPricingCalendarDetail(propertyGroupId, propertyId, date);
  const problem = useApiProblem(detail.error);
  const ownership = useDynamicPricingOwnershipRelease(propertyId, date);
  // Ohne geladene Schicht trägt die Tagesantwort selbst Name, Währung und Zeitzone.
  const apartment = layer?.apartment(propertyId) ?? (detail.data ? fromDetail(detail.data) : null);
  const day = detail.data?.day ?? layer?.day(propertyId, date) ?? null;
  if (!apartment || !day) {
    return (
      <p
        role={detail.isError ? 'alert' : 'status'}
        className="bg-paper rounded-tile text-ink-soft p-3.5 text-[13px]"
      >
        {detail.isError
          ? problemText(problem, 'Das Tagesdetail der Preisautomatik konnte nicht geladen werden.')
          : 'Tagesdetail der Preisautomatik wird geladen …'}
      </p>
    );
  }
  return (
    <section aria-label="Tagesdetail der Preisautomatik" className="bg-paper rounded-tile -mx-1">
      <p className="text-ink-soft px-5 pt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
        Tagesdetail
      </p>
      <DynamicPricingDayDetailsBody
        apartment={apartment}
        day={day}
        detail={detail.data}
        detailLoading={detail.isLoading}
        detailError={detail.isError}
        onReleaseOwnership={canEdit ? ownership.release : undefined}
        releasingField={ownership.releasingField}
        releasePending={ownership.releasePending}
        releaseError={ownership.releaseError}
      />
    </section>
  );
}

function fromDetail(
  detail: DynamicPricingCalendarDayDetailResponse,
): DynamicPricingCalendarApartment {
  return {
    propertyId: detail.propertyId,
    propertyGroupId: detail.propertyGroupId,
    name: detail.apartmentName,
    currency: detail.currency,
    timezone: detail.timezone,
    days: [],
    bookingSpans: [],
  };
}
